<script setup lang="ts">
import type { NavItem } from '~/types/ui/uiTypes'

const props = withDefaults(defineProps<{
  variant?: 'desktop' | 'mobile'
  navItems?: NavItem[]
}>(), {
  variant: 'desktop',
  navItems: () => [],
})

const route = useRoute()
const shouldPrefetchNavLinks = computed(() => route.path !== '/')

const headerClasses = computed(() =>
  props.variant === 'mobile'
    ? 'sticky top-0 z-40 h-14 border-b border-rule-soft bg-paper-2/70 text-ink backdrop-blur-lg'
    : 'sticky top-0 z-40 border-b border-rule-soft bg-paper-2/70 text-ink backdrop-blur-lg',
)

const containerClasses = computed(() =>
  props.variant === 'mobile'
    ? 'mx-auto h-full max-w-6xl w-full flex items-center justify-between px-4 sm:px-6'
    : 'mx-auto max-w-6xl flex items-center justify-between px-4 py-3 sm:px-6',
)

const logoSize = computed(() =>
  props.variant === 'mobile' ? 'h-7 w-7' : 'h-8 w-8',
)
</script>

<template>
  <header class="app-nav-header" :class="headerClasses">
    <div :class="containerClasses">
      <!-- Brand -->
      <!-- Named by its visible "Avow", so a voice-control user can say what they see. -->
      <NuxtLink to="/" :prefetch="false" class="flex items-center gap-2">
        <div :class="logoSize" aria-hidden="true">
          <slot name="logo">
            <UIAppLogo />
          </slot>
        </div>
        <UIText as="span" variant="title" :class="props.variant === 'mobile' ? 'tracking-tight' : 'text-lg'">
          Avow
        </UIText>
      </NuxtLink>

      <!-- Nav (desktop only) -->
      <nav v-if="props.variant === 'desktop' && props.navItems.length > 0" class="hidden items-center gap-1 md:flex" role="navigation" aria-label="Primary">
        <UIButton
          v-for="item in props.navItems"
          :key="item.path"
          variant="ghost"
          size="sm"
          :to="item.path"
          :prefetch="shouldPrefetchNavLinks"
          :active="isActivePath(route.path, item.path)"
          :class="{ 'text-accent-2': isActivePath(route.path, item.path) }"
        >
          {{ item.label }}
        </UIButton>
      </nav>

      <!-- Actions -->
      <div class="flex items-center gap-2">
        <slot name="actions" />
      </div>
    </div>
  </header>
</template>
